import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';
import { JsonObjectFactoryService } from '../shared/_services/json-object-factory.service';

@Injectable({
  providedIn: 'root'
})
export class StateEntityGroupsService {

  constructor(
    private http: HttpClient,
    private jsonObjectFactory: JsonObjectFactoryService
  ) { }

  /**
   * Get scenario-related parameters (scenarioCode and is_issue_key_enabled)
   */
  private getScenarioParams(globalService: any): any {
    const additionalParams: any = {};
    
    if (globalService?.workspaceFactory?.activeScreen?.filters &&
        typeof globalService.workspaceFactory.activeScreen.filters.getFilters === 'function') {
      const foundScenario: any = _.find(
        globalService.workspaceFactory.activeScreen.filters.getFilters(),
        { param_name: 'scenario' }
      );
      if (foundScenario?.selected) {
        additionalParams.scenarioCode = foundScenario.selected.CODE;
        additionalParams.is_issue_key_enabled = foundScenario.selected.ISSUE_ID_ENABLED;
      }
    }
    
    return additionalParams;
  }

  /**
   * Fetch ME (Member Entity) list
   * Uses JsonObjectFactory to automatically include filterParams and globalParams
   */
  fetchMeList(baseUrl: string, globalService: any): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_id=31741`;
    const additionalParams = this.getScenarioParams(globalService);
    return this.jsonObjectFactory.getJSONObj(url, additionalParams, globalService);
  }

  /**
   * Fetch LEID and CDR list
   * Uses JsonObjectFactory to automatically include filterParams and globalParams
   */
  fetchLeidCdrList(baseUrl: string, globalService: any): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_id=31730`;
    const additionalParams = this.getScenarioParams(globalService);
    return this.jsonObjectFactory.getJSONObj(url, additionalParams, globalService);
  }

  /**
   * Fetch Reporting Period list
   * Uses JsonObjectFactory to automatically include filterParams and globalParams
   */
  fetchReportingPeriod(baseUrl: string, globalService: any): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_id=31731`;
    const additionalParams = this.getScenarioParams(globalService);
    return this.jsonObjectFactory.getJSONObj(url, additionalParams, globalService);
  }

  /**
   * Fetch US State list
   * Uses JsonObjectFactory to automatically include filterParams and globalParams
   */
  fetchUSStateList(baseUrl: string, globalService: any): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=oppj8b`;
    const additionalParams = this.getScenarioParams(globalService);
    return this.jsonObjectFactory.getJSONObj(url, additionalParams, globalService);
  }

  /**
   * Save External State Entity (Create or Update)
   */
  saveExternalStateEntity(
    baseUrl: string, 
    entityData: any[], 
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_id=31721`;
    
    // Build settings object
    const jsonSettings = rowData && Object.keys(rowData).length > 0 ? {
      "tax_year": rowData.TAX_YEAR,
      "scenario": rowData.SCENARIO,
      "jcd_key": rowData.JCD_KEY,
      "combination_key": rowData.COMBINATION_KEY
    } : {};

    // Build params with proper fallbacks
    // filterParams now contains scenarioCode and is_issue_key_enabled (added by AngularJS)
    let params: any = _.merge({}, globalService?.globalParams || {}, filterParams);
    params.jsonObj = JSON.stringify(entityData);
    params.jsonSettings = JSON.stringify(jsonSettings);

    // Use application/x-www-form-urlencoded
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Delete External State Entity
   */
  deleteExternalStateEntity(
    baseUrl: string, 
    leKeys: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_id=31722`;
    
    // Build params with proper fallbacks
    let params: any = _.merge({}, globalService?.globalParams || {}, filterParams);
    params.le_keys = leKeys;
    params.scenario = filterParams.scenario || params.scenario;

    // Use application/x-www-form-urlencoded
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Save State Group (Create)
   * Action ID: 31723
   */
  saveStateGroup(
    baseUrl: string,
    groupData: any[],
    globalService: any,
    filterParams: any = {},
    userSettings: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_id=31723`;

    // Build settings object
    const jsonSettings = {
      "tax_year": filterParams.tax_year,
      "scenario": filterParams.scenario,
      "jcd_key": globalService?.workspaceFactory?.activeScreen?.jcd_key || globalService?.globalParams?.jcd_key,
      "combination_key": ''
    };

    // Build params with proper fallbacks
    let params: any = _.merge({}, globalService?.globalParams || {}, filterParams);
    params.jsonSettings = JSON.stringify(jsonSettings);
    params.data = JSON.stringify(groupData);
    params.sso_id = userSettings?.user?.sso_id || '';
    params.scenario = filterParams.scenario || params.scenario;
    params.tax_year = filterParams.tax_year || params.tax_year;

    // Use application/x-www-form-urlencoded
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Update State Group (Edit)
   * Action ID: 31724
   */
  updateStateGroup(
    baseUrl: string,
    groupData: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {},
    userSettings: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_id=31724`;

    // Build settings object from rowData
    const jsonSettings = {
      "tax_year": rowData.TAX_YEAR || filterParams.tax_year,
      "scenario": filterParams.scenario,
      "jcd_key": rowData.JCD_KEY || globalService?.globalParams?.jcd_key,
      "combination_key": rowData.COMBINATION_KEY || ''
    };

    // Build checkLockedbj for locking mechanism
    // For state groups, we use the first group's data for locking
    const firstGroup = groupData && groupData.length > 0 ? groupData[0] : rowData;
    const checkLockedbj = [{
      "mef_group_key": firstGroup.MEF_GROUP_KEY || '',
      "combination_key": firstGroup.COMBINATION_KEY || '',
      "scenario": firstGroup.SCENARIO || filterParams.scenario,
      "tax_year": firstGroup.TAX_YEAR || filterParams.tax_year,
      "jcd_key": firstGroup.JCD_KEY || globalService?.globalParams?.jcd_key
    }];

    // Build params with proper fallbacks
    let params: any = _.merge({}, globalService?.globalParams || {}, filterParams);
    params.data = JSON.stringify(groupData);
    params.jsonSettings = JSON.stringify(jsonSettings);
    params.checkLockedbj = JSON.stringify(checkLockedbj);
    params.sso_id = userSettings?.user?.sso_id || '';
    params.scenario = filterParams.scenario || params.scenario;
    params.tax_year = filterParams.tax_year || params.tax_year;

    // Use application/x-www-form-urlencoded
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Delete State Groups
   * Action ID: 31825
   */
  deleteStateGroups(
    baseUrl: string,
    groupKeys: string,
    globalService: any,
    filterParams: any = {},
    userSettings: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_id=31825`;

    // Build settings object
    const jsonSettings = {
      "tax_year": filterParams.tax_year,
      "scenario": filterParams.scenario,
      "jcd_key": globalService?.workspaceFactory?.activeScreen?.jcd_key || globalService?.globalParams?.jcd_key,
      "combination_key": ''
    };

    // Build checkLockedbj for locking mechanism (for delete, use filter params)
    const checkLockedbj = [{
      "combination_key": '',
      "scenario": filterParams.scenario,
      "tax_year": filterParams.tax_year,
      "jcd_key": globalService?.workspaceFactory?.activeScreen?.jcd_key || globalService?.globalParams?.jcd_key
    }];

    // Build params with proper fallbacks
    let params: any = _.merge({}, globalService?.globalParams || {}, filterParams);
    params.jsonSettings = JSON.stringify(jsonSettings);
    params.groupKeys = groupKeys;
    params.checkLockedbj = JSON.stringify(checkLockedbj);
    params.sso_id = userSettings?.user?.sso_id || '';
    params.scenario = filterParams.scenario || params.scenario;
    params.tax_year = filterParams.tax_year || params.tax_year;

    // Use application/x-www-form-urlencoded
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Load All Entities (Internal or External)
   * Action ID: 31762 (External), 31726 (Internal)
   */
  loadAllEntities(
    baseUrl: string,
    actionId: string,
    groupKey: string,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_id=${actionId}`;

    const additionalParams = {
      "tax_year": rowData.TAX_YEAR || filterParams.tax_year,
      "scenario": filterParams.scenario,
      "jcd_key": rowData.JCD_KEY || globalService?.globalParams?.jcd_key,
      "combination_key": rowData.COMBINATION_KEY || '',
      "groupKeys": [groupKey]
    };

    return this.jsonObjectFactory.getJSONObj(url, additionalParams, globalService);
  }

  /**
   * Load All Assigned Entities
   * Action ID: 31727
   */
  loadAllAssignedEntities(
    baseUrl: string,
    groupKey: string,
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_id=31727`;

    const additionalParams = {
      "tax_year": rowData.TAX_YEAR || filterParams.tax_year,
      "scenario": filterParams.scenario,
      "jcd_key": rowData.JCD_KEY || globalService?.globalParams?.jcd_key,
      "combination_key": rowData.COMBINATION_KEY || '',
      "groupKeys": [groupKey]
    };

    return this.jsonObjectFactory.getJSONObj(url, additionalParams, globalService);
  }

  /**
   * Assign Entity to Group
   * Action ID: 31734
   */
  assignEntityToGroup(
    baseUrl: string,
    entityData: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {},
    userSettings: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_id=31734`;

    // Build settings object
    const jsonSettings = {
      "tax_year": rowData.TAX_YEAR || filterParams.tax_year,
      "scenario": filterParams.scenario,
      "jcd_key": rowData.JCD_KEY || globalService?.globalParams?.jcd_key,
      "combination_key": rowData.COMBINATION_KEY || ''
    };

    // Build checkLockedbj for locking mechanism
    const checkLockedbj = [{
      "mef_group_key": rowData.MEF_GROUP_KEY || '',
      "combination_key": rowData.COMBINATION_KEY || '',
      "scenario": filterParams.scenario,
      "tax_year": rowData.TAX_YEAR || filterParams.tax_year,
      "jcd_key": rowData.JCD_KEY || globalService?.globalParams?.jcd_key
    }];

    // Build params with proper fallbacks
    let params: any = _.merge({}, globalService?.globalParams || {}, filterParams);
    params.data = JSON.stringify(entityData);
    params.jsonSettings = JSON.stringify(jsonSettings);
    params.checkLockedbj = JSON.stringify(checkLockedbj);
    params.sso_id = userSettings?.user?.sso_id || '';
    params.scenario = filterParams.scenario || params.scenario;
    params.tax_year = filterParams.tax_year || params.tax_year;

    // Use application/x-www-form-urlencoded
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Unassign Entity from Group
   * Action ID: 31739
   */
  unassignEntityFromGroup(
    baseUrl: string,
    entityData: any,
    globalService: any,
    filterParams: any = {},
    rowData: any = {},
    userSettings: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_id=31739`;

    // Build settings object
    const jsonSettings = {
      "tax_year": rowData.TAX_YEAR || filterParams.tax_year,
      "scenario": filterParams.scenario,
      "jcd_key": rowData.JCD_KEY || globalService?.globalParams?.jcd_key,
      "combination_key": rowData.COMBINATION_KEY || ''
    };

    // Build checkLockedbj for locking mechanism
    const checkLockedbj = [{
      "mef_group_key": rowData.MEF_GROUP_KEY || '',
      "combination_key": rowData.COMBINATION_KEY || '',
      "scenario": filterParams.scenario,
      "tax_year": rowData.TAX_YEAR || filterParams.tax_year,
      "jcd_key": rowData.JCD_KEY || globalService?.globalParams?.jcd_key
    }];

    // Build params with proper fallbacks
    let params: any = _.merge({}, globalService?.globalParams || {}, filterParams);
    params.data = JSON.stringify(entityData);
    params.jsonSettings = JSON.stringify(jsonSettings);
    params.checkLockedbj = JSON.stringify(checkLockedbj);
    params.sso_id = userSettings?.user?.sso_id || '';
    params.scenario = filterParams.scenario || params.scenario;
    params.tax_year = filterParams.tax_year || params.tax_year;

    // Use application/x-www-form-urlencoded
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }

  /**
   * Save Address for Assigned Entities
   * Action ID: 31867
   */
  saveAssignedEntityAddress(
    baseUrl: string,
    entityData: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_id=31867`;

    // Build checkLockedbj for locking mechanism
    const checkLockedbj = [{
      "mef_group_key": rowData.MEF_GROUP_KEY || '',
      "combination_key": rowData.COMBINATION_KEY || '',
      "scenario": filterParams.scenario,
      "tax_year": rowData.TAX_YEAR || filterParams.tax_year,
      "jcd_key": rowData.JCD_KEY || globalService?.globalParams?.jcd_key
    }];

    // Build params with proper fallbacks
    let params: any = _.merge({}, globalService?.globalParams || {}, filterParams);
    params.data = JSON.stringify(entityData);
    params.checkLockedbj = JSON.stringify(checkLockedbj);
    params.scenario = filterParams.scenario || params.scenario;
    params.tax_year = rowData.TAX_YEAR || filterParams.tax_year;
    params.jcd_key = rowData.JCD_KEY || globalService?.globalParams?.jcd_key;

    // Use application/x-www-form-urlencoded
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }
}
